// bluaIQ — B.5 Permits: the OWN-CLOCK entity that GENERATES tasks.
//
// TWO CLOCKS, sealed: a permit can expire without any Gantt task changing.
// The server derives `effectiveStatus` at READ time (never a stored EXPIRED,
// never a cron) — this UI renders what the server derived and NEVER computes
// expiry itself: one derivation, one owner (permitLifecycle.ts).
//
// The status clock moves ONLY through /transition (the graph decides, 409 on
// conflict). Task generation is an EXPLICIT button — never a side effect of a
// transition (no-silent-write).

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FileCheck2, Plus, ArrowRight, CalendarPlus, AlertTriangle, X } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

interface Permit {
  id: string
  jurisdiction: string
  permitType: string
  permitNumber?: string | null
  status: string
  /** Server-derived (two clocks): EXPIRED here never exists in `status`. */
  effectiveStatus: string
  submittedAt?: string | null
  issuedAt?: string | null
  finaledAt?: string | null
  expiresAt?: string | null
  feeAmount?: string | number | null
  notes?: string | null
}

// Mirror of PERMIT_TRANSITIONS (permitLifecycle.ts) for BUTTON OFFERING only —
// the server's graph remains the authority; a stale offer just gets a 409.
const NEXT: Record<string, string[]> = {
  DRAFT: ['SUBMITTED'],
  SUBMITTED: ['IN_REVIEW'],
  IN_REVIEW: ['CORRECTIONS', 'ISSUED'],
  CORRECTIONS: ['IN_REVIEW'],
  ISSUED: ['INSPECTIONS'],
  INSPECTIONS: ['FINALED'],
  FINALED: [],
}

const STATUS_LABEL: Record<string, { en: string; es: string; color: string }> = {
  DRAFT: { en: 'Draft', es: 'Borrador', color: '#9E9890' },
  SUBMITTED: { en: 'Submitted', es: 'Presentado', color: '#06539B' },
  IN_REVIEW: { en: 'In review', es: 'En revisión', color: '#0288D1' },
  CORRECTIONS: { en: 'Corrections requested', es: 'Correcciones solicitadas', color: '#F59E0B' },
  ISSUED: { en: 'Issued', es: 'Emitido', color: '#22C55E' },
  INSPECTIONS: { en: 'Inspections', es: 'Inspecciones', color: '#00BCD4' },
  FINALED: { en: 'Finaled', es: 'Finalizado', color: '#1C1A17' },
  EXPIRED: { en: 'Expired', es: 'Vencido', color: '#DC2626' },
}

const PERMIT_TYPE_LABEL: Record<string, { en: string; es: string }> = {
  BUILDING: { en: 'Building', es: 'Construcción' },
  ELECTRICAL: { en: 'Electrical', es: 'Electricidad' },
  PLUMBING: { en: 'Plumbing', es: 'Instalaciones hidráulicas y sanitarias' },
  MECHANICAL: { en: 'Mechanical', es: 'Instalaciones mecánicas' },
  IRRIGATION: { en: 'Irrigation', es: 'Riego' },
  POOL: { en: 'Pool', es: 'Piscina' },
  FENCE: { en: 'Fence', es: 'Cercado' },
  OTHER: { en: 'Other', es: 'Otro' },
}

const PERMIT_TYPES = Object.keys(PERMIT_TYPE_LABEL)

export default function PermitsModule({ authTokenOverride }: { authTokenOverride?: string | null } = {}) {
  const { getToken } = useAuth()
  const { i18n } = useTranslation()
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const isEs = i18n.language.startsWith('es')
  const isDemo = isDemoProject(projectId)

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ jurisdiction: '', permitType: 'BUILDING', permitNumber: '', expiresAt: '', feeAmount: '', notes: '' })
  const [actionError, setActionError] = useState<string | null>(null)

  const authed = async () => {
    const token = authTokenOverride !== undefined ? authTokenOverride : await getToken()
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' }
  }

  const { data: permits, isLoading, isError, refetch } = useQuery<Permit[]>({
    queryKey: ['permits', projectId],
    queryFn: async () =>
      apiFetch(`/api/projects/${projectId}/permits`, { headers: await authed() }).then((r) => {
        if (!r.ok) throw new Error('API unavailable')
        return r.json()
      }),
    enabled: !isDemo,
    retry: false,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['permits', projectId] })

  const onError = (err: Error) => setActionError(err.message)

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { jurisdiction: form.jurisdiction, permitType: form.permitType }
      if (form.permitNumber) body.permitNumber = form.permitNumber
      if (form.expiresAt) body.expiresAt = new Date(form.expiresAt).toISOString()
      if (form.feeAmount) body.feeAmount = Number(form.feeAmount)
      if (form.notes) body.notes = form.notes
      const r = await apiFetch(`/api/projects/${projectId}/permits`, { method: 'POST', headers: await authed(), body: JSON.stringify(body) })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || (isEs ? 'No se pudo crear' : 'Could not create'))
      return r.json()
    },
    onSuccess: () => { invalidate(); setShowCreate(false); setForm({ jurisdiction: '', permitType: 'BUILDING', permitNumber: '', expiresAt: '', feeAmount: '', notes: '' }); setActionError(null) },
    onError,
  })

  const transitionMutation = useMutation({
    mutationFn: async ({ permitId, to }: { permitId: string; to: string }) => {
      const r = await apiFetch(`/api/projects/${projectId}/permits/${permitId}/transition`, { method: 'POST', headers: await authed(), body: JSON.stringify({ to }) })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        // 409 = the graph refused or the permit moved under us — reload truth.
        throw new Error(e.message || e.error || (isEs ? 'Transición rechazada' : 'Transition refused'))
      }
      return r.json()
    },
    onSuccess: () => { invalidate(); setActionError(null) },
    onError: (err: Error) => { invalidate(); onError(err) },
  })

  const generateMutation = useMutation({
    mutationFn: async (permitId: string) => {
      const r = await apiFetch(`/api/projects/${projectId}/permits/${permitId}/generate-tasks`, { method: 'POST', headers: await authed() })
      if (!r.ok) throw new Error(isEs ? 'No se pudieron generar tareas' : 'Could not generate tasks')
      return r.json() as Promise<{ created: number; skipped: number }>
    },
    onSuccess: (res) => {
      setActionError(null)
      // Honest count, denominator visible: created AND skipped, never just "done".
      setLastGenerated(isEs ? `${res.created} tareas creadas · ${res.skipped} ya existían` : `${res.created} tasks created · ${res.skipped} already existed`)
      queryClient.invalidateQueries({ queryKey: ['lifecycle', projectId] })
    },
    onError,
  })
  const [lastGenerated, setLastGenerated] = useState<string | null>(null)

  const L = (k: string) => STATUS_LABEL[k] ? (isEs ? STATUS_LABEL[k].es : STATUS_LABEL[k].en) : k
  const permitTypeLabel = (k: string) => PERMIT_TYPE_LABEL[k] ? (isEs ? PERMIT_TYPE_LABEL[k].es : PERMIT_TYPE_LABEL[k].en) : k
  const locale = isEs ? 'es' : 'en'

  if (isDemo) return <SavedProjectOnlyNotice testId="permits-saved-only" bodyEn="Permits are tracked on saved projects only." bodyEs="Los permisos se rastrean solo en proyectos guardados." />

  return (
    <div className="p-4 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17] flex items-center gap-2">
          <FileCheck2 size={18} /> {isEs ? 'Permisos' : 'Permits'}
        </h2>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="flex items-center gap-1.5 bg-[#06539B] text-white rounded-full px-4 py-2 text-[12px] font-[800]"
        >
          <Plus size={14} /> {isEs ? 'Nuevo permiso' : 'New permit'}
        </button>
      </div>
      <p className="text-[11px] font-[600] text-[#9E9890]">
        {isEs
          ? 'El permiso tiene su propio plazo: puede vencer sin que cambie ninguna tarea del cronograma.'
          : 'A permit runs its own clock — it can expire without any schedule task changing.'}
      </p>

      {actionError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-[12px] font-[700] text-red-700">
          <AlertTriangle size={14} /> {actionError}
          <button onClick={() => setActionError(null)} className="ml-auto" aria-label={isEs ? 'Cerrar' : 'Dismiss'}><X size={14} /></button>
        </div>
      )}
      {lastGenerated && (
        <div className="bg-[#F7FDFE] border border-[#D8F6FA] rounded-xl px-3 py-2 text-[12px] font-[700] text-[#1C1A17]">
          {lastGenerated}
        </div>
      )}

      {showCreate && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Ciudad o autoridad competente (AHJ)' : 'City / Jurisdiction (AHJ)'} *
              <input value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]" placeholder={isEs ? 'p. ej., Ciudad de Dallas' : 'e.g. City of Dallas'} />
            </label>
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Tipo' : 'Type'} *
              <select value={form.permitType} onChange={(e) => setForm({ ...form, permitType: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] bg-white">
                {PERMIT_TYPES.map((t) => <option key={t} value={t}>{permitTypeLabel(t)}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Número (si ya existe)' : 'Number (if assigned)'}
              <input value={form.permitNumber} onChange={(e) => setForm({ ...form, permitNumber: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]" />
            </label>
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Vence' : 'Expires'}
              <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]" />
            </label>
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Tarifa (USD)' : 'Fee (USD)'}
              <input type="number" min="0" step="0.01" value={form.feeAmount} onChange={(e) => setForm({ ...form, feeAmount: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]" />
            </label>
            <label className="text-[11px] font-[800] text-[#1C1A17]">
              {isEs ? 'Notas' : 'Notes'}
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]" />
            </label>
          </div>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!form.jurisdiction.trim() || createMutation.isPending}
            className="bg-[#06539B] text-white rounded-full px-5 py-2 text-[12px] font-[800] disabled:opacity-50"
          >
            {createMutation.isPending ? (isEs ? 'Creando…' : 'Creating…') : (isEs ? 'Crear permiso' : 'Create permit')}
          </button>
        </div>
      )}

      {isLoading && <p className="text-[12px] font-[600] text-[#9E9890]">{isEs ? 'Cargando…' : 'Loading…'}</p>}
      {!isLoading && isError && (
        <div data-testid="permits-load-error" className="border border-red-200 bg-red-50 p-4 text-red-800">
          <p className="text-[12px] font-[800]">{isEs ? 'No se pudieron cargar los permisos.' : 'Permits could not be loaded.'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-[11px] font-[800] underline underline-offset-2"
          >
            {isEs ? 'Volver a intentar' : 'Try again'}
          </button>
        </div>
      )}
      {!isLoading && !isError && (!permits || permits.length === 0) && !showCreate && (
        <div className="text-center py-12">
          <FileCheck2 size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{isEs ? 'Sin permisos todavía' : 'No permits yet'}</p>
        </div>
      )}

      <div className="space-y-3">
        {(permits || []).map((p) => {
          const eff = STATUS_LABEL[p.effectiveStatus] || STATUS_LABEL.DRAFT
          const expired = p.effectiveStatus === 'EXPIRED'
          const nexts = NEXT[p.status] || []
          return (
            <div key={p.id} className={`bg-white border rounded-xl p-4 space-y-2 ${expired ? 'border-red-300' : 'border-[#ECEAE4]'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-[900] text-[#1C1A17]">{permitTypeLabel(p.permitType)}</span>
                <span className="text-[12px] font-[700] text-[#9E9890]">· {p.jurisdiction}</span>
                {p.permitNumber && <span className="text-[11px] font-[600] text-[#9E9890]">#{p.permitNumber}</span>}
                <span className="ml-auto inline-flex items-center text-[10px] font-[800] px-2.5 py-0.5 rounded-full text-white" style={{ background: eff.color }}>
                  {L(p.effectiveStatus)}
                </span>
              </div>
              {expired && (
                <p className="text-[11px] font-[700] text-red-700 flex items-center gap-1.5">
                  <AlertTriangle size={12} />
                  {isEs
                    ? `Venció el ${new Date(p.expiresAt!).toLocaleDateString(locale)}. El estado guardado sigue en “${L(p.status)}”; el vencimiento se determina al consultar.`
                    : `Expired ${new Date(p.expiresAt!).toLocaleDateString(locale)} — stored stage is still "${L(p.status)}"; expiry is derived at read.`}
                </p>
              )}
              <div className="flex flex-wrap gap-3 text-[10px] font-[600] text-[#9E9890]">
                {p.submittedAt && <span>{isEs ? 'Presentado' : 'Submitted'}: {new Date(p.submittedAt).toLocaleDateString(locale)}</span>}
                {p.issuedAt && <span>{isEs ? 'Emitido' : 'Issued'}: {new Date(p.issuedAt).toLocaleDateString(locale)}</span>}
                {p.expiresAt && !expired && <span>{isEs ? 'Vence' : 'Expires'}: {new Date(p.expiresAt).toLocaleDateString(locale)}</span>}
                {p.feeAmount != null && <span>{isEs ? 'Tarifa' : 'Fee'}: ${Number(p.feeAmount).toFixed(2)}</span>}
              </div>
              {p.notes && <p className="text-[11px] font-[600] text-[#1C1A17]">{p.notes}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                {nexts.map((to) => (
                  <button
                    key={to}
                    onClick={() => transitionMutation.mutate({ permitId: p.id, to })}
                    disabled={transitionMutation.isPending}
                    className="flex items-center gap-1 border border-[#06539B] text-[#06539B] hover:bg-[#06539B] hover:text-white rounded-full px-3 py-1.5 text-[11px] font-[800] disabled:opacity-50"
                  >
                    <ArrowRight size={12} /> {L(to)}
                  </button>
                ))}
                {(NEXT[p.status] !== undefined && (['DRAFT', 'SUBMITTED', 'CORRECTIONS', 'ISSUED', 'INSPECTIONS'].includes(p.status))) && (
                  <button
                    onClick={() => generateMutation.mutate(p.id)}
                    disabled={generateMutation.isPending}
                    className="flex items-center gap-1 border border-[#ECEAE4] text-[#1C1A17] hover:bg-[#F2F0EB] rounded-full px-3 py-1.5 text-[11px] font-[800] disabled:opacity-50"
                    title={isEs ? 'Agrega al cronograma las tareas de esta etapa sin duplicarlas' : "Generate this stage's Gantt tasks without duplicates"}
                  >
                    <CalendarPlus size={12} /> {isEs ? 'Agregar tareas de la etapa' : 'Generate stage tasks'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
